import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, LanguageToggle } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const ForgotPasswordPage = () => {
  const { i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-misel-bg flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="flex justify-between items-start mb-8">
          <Link to="/login" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            <span className="font-roboto font-bold tracking-wider">MISEL GROUP DIGITAL LAB</span>
          </Link>
          <LanguageToggle />
        </div>

        {sent ? (
          <div className="text-center">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
            <h1 className="font-roboto font-bold text-2xl text-white mb-4">
              {i18n.language === 'el' ? 'Email Απεστάλη' : 'Email Sent'}
            </h1>
            <p className="text-gray-400 mb-8">
              {i18n.language === 'el' 
                ? 'Αν το email υπάρχει στο σύστημα, θα λάβεις οδηγίες για επαναφορά κωδικού.'
                : 'If the email exists in our system, you will receive password reset instructions.'
              }
            </p>
            <Link to="/login">
              <Button className="bg-primary hover:bg-primary/90">
                {i18n.language === 'el' ? 'Επιστροφή στη Σύνδεση' : 'Back to Login'}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-roboto font-bold text-2xl text-white mb-2">
              {i18n.language === 'el' ? 'Επαναφορά Κωδικού' : 'Reset Password'}
            </h1>
            <p className="text-gray-400 mb-8">
              {i18n.language === 'el' 
                ? 'Εισάγετε το email σας για να λάβετε link επαναφοράς'
                : 'Enter your email to receive a reset link'
              }
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                    placeholder="email@example.com"
                    required
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold tracking-wide uppercase py-6"
              >
                {loading 
                  ? (i18n.language === 'el' ? 'Αποστολή...' : 'Sending...') 
                  : (i18n.language === 'el' ? 'Αποστολή Link' : 'Send Reset Link')
                }
              </Button>
            </form>

            <div className="mt-8 text-center">
              <Link to="/login" className="text-primary hover:underline">
                {i18n.language === 'el' ? 'Επιστροφή στη Σύνδεση' : 'Back to Login'}
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
